import React, { useMemo } from 'react';
import cn from 'classnames';
import Slider from 'rc-slider';
import { observer } from 'store';
import { defaultDurationMarks } from './constants';
import Title from 'components/UI/Title';
import Subtitle from 'components/UI/Subtitle';
import { useRangeStepsState } from '../../../hooks';
import { getDefaultBudgetMarks, getDefaultTicketsMarks } from './utils';
import { useLang, useTrans } from 'utils/localisation';
import 'rc-slider/assets/index.css';
import './RangeSliderMobile.scss';
import * as styles from './RangeSliderMobile.module.scss';

function Mobile({ subtitleMobile, calculatorID, onChangeCount, type, initialStep = 1 }) {
  const [step, setStep] = useRangeStepsState(type, initialStep, calculatorID);
  const lang = useLang();
  const t = useTrans(['calculator', 'common']);
  const currency = lang === 'de' ? 'EUR' : 'USD';

  const sliderProps = useMemo(() => {
    switch (type) {
      case 'tickets':
        return {
          onChange: value => {
            const outsideRange = value < 1 ? 1 : 5;
            const withinRange = value >= 1 && value <= 5;
            const _value = withinRange ? value : outsideRange;
            const tickets = getDefaultTicketsMarks(t('upTo'), t('tickets'), t('over'))[
              _value
            ].includes('tickets')
              ? getDefaultTicketsMarks(t('upTo'), t('tickets'), t('over'))[_value]
              : `${getDefaultTicketsMarks(t('upTo'), t('tickets'), t('over'))[_value]} ${t(
                  'tickets',
                )}`;

            setStep(_value);
            onChangeCount(tickets, _value);
          },
          min: 0,
          max: 5,
          marks: getDefaultTicketsMarks(t('upTo'), t('tickets'), t('over')),
          step: null,
          value: step,
          defaultValue: 1,
        };
      case 'budget':
        return {
          onChange: value => {
            const outsideRange = value < 1 ? 1 : 5;
            const withinRange = value >= 1 && value <= 5;
            const _value = withinRange ? value : outsideRange;
            const budget = `${
              getDefaultBudgetMarks(t('upTo'), t('k'), t('over'))[_value]
            } ${currency}`;

            setStep(_value);
            onChangeCount(budget, _value);
          },
          min: 0,
          max: 5,
          marks: getDefaultBudgetMarks(t('upTo'), t('k'), t('over')),
          step: null,
          value: step,
          defaultValue: 1,
        };
      case 'duration':
        return {
          onChange: value => {
            const _value = Math.min(24, Math.max(1, value));
            const duration = _value === 1 ? `${value} ${t('month')}` : `${value} ${t('months')}`;

            setStep(_value);
            onChangeCount(duration, _value);
          },
          marks: defaultDurationMarks,
          min: 1,
          max: 24,
          step: 1,
          value: step,
          defaultValue: 6,
        };
      default:
        return null;
    }
  }, [step, type]);

  const renderTitles = useMemo(() => {
    switch (type) {
      case 'tickets':
        return (
          <Title tag="h2" className={cn(styles.title, styles[type])}>
            {getDefaultTicketsMarks(t('upTo'), t('tickets'), t('over'))[step].includes('tickets')
              ? getDefaultTicketsMarks(t('upTo'), t('tickets'), t('over'))[step]
              : `${getDefaultTicketsMarks(t('upTo'), t('tickets'), t('over'))[step]} ${t(
                  'tickets',
                )}`}
          </Title>
        );
      case 'budget':
        return (
          <Title tag="h2" className={cn(styles.title, styles[type])}>
            {`${getDefaultBudgetMarks(t('upTo'), t('k'), t('over'))[step]} ${currency}`}
          </Title>
        );
      case 'duration':
        return (
          <>
            <Title tag="h2" className={cn(styles.title, styles[type])}>
              <span className={styles.step}>{step}</span>
              <span className={cn(styles.step, styles.months)}>
                {step === 1 ? t('month') : t('months')}
              </span>
            </Title>
            <Title tag="h5" className={styles.rangeTitle}>
              {t('durationMonths')}
            </Title>
          </>
        );
      default:
        return null;
    }
  }, [type, step]);

  return (
    <>
      <Subtitle size="lg" className={cn(styles.description, styles[type])}>
        {subtitleMobile}
      </Subtitle>
      {renderTitles}
      <div className={cn(`calculator-slider-mobile-${type}`, styles.container, styles[type])}>
        <Slider className={cn(styles.slider, styles[type])} {...sliderProps} />
      </div>
    </>
  );
}

export default observer(Mobile);
